import { ITextFrameCharacterMetadata, ITextFrameParagraphMetadata, metadataServiceInstance } from "@/services";
import { trimStart, trimEnd } from "@/common/utilities";

const defaultParagraphMetadata: ITextFrameParagraphMetadata = {
    "AutoHyphenate": true,
    "AutoLeadingPercentage": 1.2,
    "AutoTCY": 0,
    "BurasagariType": 0,
    "BurniKinshi": true,
    "ComposerEngine": 0,
    "ConsecutiveHyphenLimit": 0,
    "DefaultTabWidth": 36,
    "DesiredGlyphScaling": 1,
    "DesiredLetterSpacing": 0,
    "DesiredWordSpacing": 1,
    "EveryLineComposer": false,
    "EndIndent": 0,
    "FirstLineIndent": 0,
    "HangingRoman": false,
    "HyphenateCapitalized": true,
    "HyphenatedWordSize": 6,
    "HyphenationPreference": 0.5,
    "HyphenationZone": 36,
    "KashidaWidth": 2,
    "KurikaeshiMojiShori": false,
    "LeadingType": 0,
    "MaxGlyphScaling": 1,
    "MaxLetterSpacing": 0,
    "MaxWordSpacing": 1.33,
    "MinGlyphScaling": 1,
    "MinLetterSpacing": 0,
    "MinWordSpacing": 0.8,
    "ParagraphDirection": 0,
    "ParagraphJustification": 0,
    "ParagraphJustificationMethod": 7,
    "PostHyphenSize": 2,
    "PreHyphenSize": 2,
    "PreferredKinsokuOrder": 0,
    "SingleWordJustification": 6,
    "SpaceAfter": 0,
    "SpaceBefore": 0,
    "StartIndent": 0,
    "content": "",
    "index": 0,
    "rangeEnd": 0,
    "rangeSize": 0,
    "rangeStart": 0
};

const defaultCharacterMetadata: ITextFrameCharacterMetadata = {
    "AlternateLigatures": false,
    "AutoKernType": 0,
    "AutoLeading": true,
    "AutoMydfb": true,
    "BaseLineShift": 0,
    "BaselineDirection": 1,
    "CharacterRotation": 0,
    "ConnectionForms": false,
    "ContextualLigatures": false,
    "DiacVPos": 4,
    "DiacXOffset": 0,
    "DiacYOffset": 0,
    "DigitSet": 0,
    "DirOverride": 0,
    "DiscretionaryLigatures": false,
    "FigureStyle": 0,
    "Fill": true,
    "FillBackground": false,
    "FillColour": {
        "colour": {
            "c": 0,
            "k": 1,
            "m": 0,
            "y": 0
        },
        "colourHint": {
            "c": 0,
            "k": 1,
            "m": 0,
            "y": 0
        },
        "colourType": 0
    },
    "FillFirst": true,
    "FillOverprint": true,
    "FillVisible": true,
    "Font": "Avenir",
    "FontBaselineOption": 0,
    "FontCapsOption": 0,
    "FontOpenTypePositionOption": 0,
    "FontSize": 7.5,
    "Fractions": false,
    "HorizontalScale": 1,
    "IsFontSubstituted": 0,
    "Italics": false,
    "JapaneseAlternateFeature": 0,
    "JustificationAlternates": false,
    "Kana": false,
    "Kashidas": 0,
    "Language": 0,
    "Leading": 9,
    "LeftAki": -1,
    "Ligature": true,
    "LineCap": 0,
    "LineDashOffset": 0,
    "LineJoin": 0,
    "LineWidth": 1,
    "MarkYdistanceFromBaseline": 100,
    "MiterLimit": 10,
    "NoBreak": false,
    "OldStyle": false,
    "Ordinals": false,
    "Ornaments": false,
    "OverlapSwash": false,
    "ProportionalMetrics": false,
    "RightAki": -1,
    "Ruby": false,
    "StretchedAlternates": false,
    "StrikethroughPosition": 0,
    "Stroke": false,
    "StyleRunAlignment": 2,
    "StylisticAlternates": false,
    "StylisticSets": 0,
    "Swash": false,
    "TCYLeftRightAdjustment": 0,
    "TCYUpDownAdjustment": 0,
    "Titling": false,
    "Tracking": 0,
    "Tsume": 0,
    "TypefaceName": "",
    "UnderlineOffset": 0,
    "UnderlinePosition": 0,
    "VerticalScale": 1,
    "WariChuEnabled": false,
    "content": "",
    "index": 0,
    "rangeEnd": 0,
    "rangeSize": 0,
    "rangeStart": 0
}

const equalityCheckExcludedFields = [
    "content", "index", "rangeStart", "rangeSize", "rangeEnd",
];

export function remapHtmlToMetadata(textElement: SVGTextElement, htmlElements: Element[]) {
    const metadataRoot = metadataServiceInstance.getMetadataForElement(textElement);
    if(!metadataRoot) {
        return;
    }

    const newParagraphMetadata: ITextFrameParagraphMetadata[] = [];
    const newCharacterMetadata: ITextFrameCharacterMetadata[] = [];

    const paragraphArchetype = metadataRoot.paragraph_metadata[0];
    const characterArchetype = metadataRoot.character_metadata[0];

    let paragraphMetaRangeStart = 0;
    let paragraphMetaIndex = -1;

    let characterMetaRangeStart = 0;
    let characterMetaIndex = -1;

    for (const paragraphElement of htmlElements) {
        if ('innerText' in paragraphElement === false) {
            continue;
        }

        let paragraphText = "";

        let firstCharGroup = true;
        for (const spanElement of paragraphElement.children) {
            if (!(spanElement instanceof HTMLElement)) {
                continue;
            }

            const spanText = firstCharGroup && paragraphMetaIndex >= 0 
                ? `\r${spanElement.innerText}` 
                : spanElement.innerText;

            paragraphText += spanText;

            const characterMeta = {
                ...defaultCharacterMetadata,
                ...characterArchetype,

                index: ++characterMetaIndex,
                content: spanText,
                rangeStart: characterMetaRangeStart,
                rangeSize: spanText.length,
                rangeEnd: characterMetaRangeStart + spanText.length,
            };

            if (spanElement.style.letterSpacing.length > 0) {
                const letterSpacing = parseFloat(spanElement.style.letterSpacing);
                characterMeta.Tracking = letterSpacing * 1000;
            }

            characterMetaRangeStart += spanText.length;
            newCharacterMetadata.push(characterMeta);
            firstCharGroup = false;
        }

        let originalLength = paragraphText.length;
        paragraphText = trimStart(paragraphText, '\r');
        paragraphMetaRangeStart += originalLength - paragraphText.length;
        paragraphText = trimEnd(paragraphText, '\r');

        const paragraph: ITextFrameParagraphMetadata = {
            ...defaultParagraphMetadata,
            ...paragraphArchetype,

            index: ++paragraphMetaIndex,
            content: paragraphText,
            rangeStart: paragraphMetaRangeStart,
            rangeSize: paragraphText.length,
            rangeEnd: paragraphMetaRangeStart + paragraphText.length,
        };

        paragraphMetaRangeStart += paragraphText.length;
        newParagraphMetadata.push(paragraph);
    }

    metadataRoot.paragraph_metadata = newParagraphMetadata;

    if(newCharacterMetadata.length >= 2) {
        metadataRoot.character_metadata = [];

        // Merge any identical newCharacterMetadata entries.
        let lastCharacterMeta = newCharacterMetadata[0];
        for (let i = 1; i < newCharacterMetadata.length; i++) {
            const characterMeta = newCharacterMetadata[i];
            let shouldCombine = true;
            for(const prop in characterMeta) {
                if(equalityCheckExcludedFields.includes(prop)) {
                    continue;
                }

                if(JSON.stringify(characterMeta[prop]) !== JSON.stringify(lastCharacterMeta[prop])) {
                    shouldCombine = false;
                    break;
                }
            }

            if(shouldCombine) {
                lastCharacterMeta.rangeSize += characterMeta.rangeSize;
                lastCharacterMeta.rangeEnd += characterMeta.rangeSize;
                lastCharacterMeta.content += characterMeta.content;
            } else {
                metadataRoot.character_metadata.push(lastCharacterMeta);
                lastCharacterMeta = characterMeta;
            }
        }

        metadataRoot.character_metadata.push(lastCharacterMeta);
    } else {
        metadataRoot.character_metadata = newCharacterMetadata;
    }

    // Trim off \r characters for each metadata entry
    let first = true;
    for(const characterMeta of metadataRoot.character_metadata) {
        const originalContent = characterMeta.content;

        characterMeta.content = trimStart(characterMeta.content, '\r');
        if(!first) {
            characterMeta.rangeStart += originalContent.length - characterMeta.content.length;
        }

        characterMeta.content = trimEnd(characterMeta.content, '\r');
        characterMeta.rangeSize = characterMeta.content.length;
        characterMeta.rangeEnd = characterMeta.rangeStart + characterMeta.rangeSize;

        first = false;
    }
}